import { useContext } from "react";
import { Box, MenuItem, FormControl, Select, IconButton } from "@mui/material";
import { T } from "../hooks/useTranslate";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { LanguageContext } from "../hooks/LanguageContext";
import { RowPageCount } from "./RowPageCount";



const CustomPagination = ({
  page,
  pageSize,
  rowCount,
  handlePageSizeChange,
  loading,
  handlePageChange,
  isPartners,
  isStatements
}) => {
  const theme = useTheme();
  const viewportIsSmall = useMediaQuery(theme.breakpoints.up("sm"));

  const { languageDir } = useContext(LanguageContext);

  const iconStyling = {
    border: "1px solid rgba(145, 158, 171, 0.2);",
    borderRadius: "4px",
    padding: 0,
    marginRight: "8px",
    transform: languageDir === "rtl" ? "rotate(180deg)" : "none",
  };

  // change pageSize
  const handleSelectDropdown = (e) => {
    handlePageChange(0);
    handlePageSizeChange(e.target.value);
  };

  // handle action button clicks
  const handleNavClick = (e) => {
    switch (e.currentTarget.name) {
      case "first-page-icon":
        handlePageChange(0);
        break;
      case "nav-before-icon":
        handlePageChange(page - 1);
        break;
      case "nav-next-icon":
        handlePageChange(page + 1);
        break;
      case "last-page-icon":
        rowCount % pageSize === 0
          ? handlePageChange(Math.floor(rowCount / pageSize - 1))
          : handlePageChange(Math.floor(rowCount / pageSize));
        break;
      default:
        break;
    }
  };

  const highestRowOnPage = (page + 1) * pageSize > rowCount ? rowCount : (page + 1) * pageSize;

  return (
    <Box
      id="pagination-container"
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "22px 15px 22px 15px",
      }}
    >
      <RowPageCount
        rowCount={rowCount}
        page={page}
        pageSize={pageSize}
        highestRowOnPage={highestRowOnPage}
      />
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{ marginRight: "24px", display: "flex", alignItems: "center" }}
        >
          {viewportIsSmall && (
            <Box
              data-test-id={`records-per-page-text`}
              sx={{ width: "120px" }}
            >
              {isPartners ? <T>partners_per_page</T> : isStatements ? <T>statements_per_page</T> : <T>members_per_page</T>}:
            </Box>
          )}
          <FormControl size="small">
            <Select
              labelId="page-size-select-label"
              id="page-size-select"
              data-test-id={`page-size-select`}
              aria-label="change page size"
              value={pageSize}
              onChange={handleSelectDropdown}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              disabled={loading || rowCount === 0 || rowCount <= 10}
              MenuProps={{
                PaperProps: { sx: paperPropsObj }
              }}
            >
              <MenuItem value={10} aria-label="select 10">10</MenuItem>
              <MenuItem value={50} aria-label="select 50">50</MenuItem>
              <MenuItem value={100} aria-label="select 100">100</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            aria-label="go to first page"
            id="first-page-button"
            data-test-id={`first-page-button`}
            onClick={handleNavClick}
            sx={iconStyling}
            disabled={loading || page === 0}
            name="first-page-icon"
          >
            <FirstPageIcon />
          </IconButton>
          <IconButton
            aria-label="go to page before"
            id="before-page-button"
            data-test-id={`before-page-button`}
            onClick={handleNavClick}
            sx={iconStyling}
            disabled={loading || page === 0}
            name="nav-before-icon"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            aria-label="go to next page"
            id="next-page-button"
            data-test-id={`next-page-button`}
            onClick={handleNavClick}
            sx={iconStyling}
            disabled={loading || highestRowOnPage === rowCount}
            name="nav-next-icon"
          >
            <NavigateNextIcon />
          </IconButton>
          <IconButton
            aria-label="go to last page"
            id="last-page-button"
            data-test-id={`last-page-button`}
            onClick={handleNavClick}
            sx={iconStyling}
            disabled={loading || highestRowOnPage === rowCount}
            name="last-page-icon"
          >
            <LastPageIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export { CustomPagination };

const paperPropsObj = {
  boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(145, 158, 171, 0.1)",
  border: "1px solid rgba(145, 158, 171, 0.1)",
  borderRadius: "8px",
  maxHeight: "500px",
  marginTop: "10px"
}